import { useEffect, useRef } from "react";

export interface UseFocusRefetchOptions {
  throttleMs?: number;
}

/**
 * Calls `callback` when the tab regains visibility or the window regains focus.
 * Throttles back-to-back triggers within `throttleMs` (default 10 000 ms) — the
 * two browser events typically fire together when a user alt-tabs back, so
 * without a throttle the callback would run twice.
 *
 * Does NOT fire on initial mount; the page already has its first fetch path.
 *
 * The callback is stored in a ref so consumers can pass an inline function
 * without re-subscribing listeners every render.
 */
export function useFocusRefetch(
  callback: () => void | Promise<void>,
  opts: UseFocusRefetchOptions = {},
): void {
  const throttleMs = opts.throttleMs ?? 10_000;
  const cbRef = useRef(callback);
  cbRef.current = callback;
  const lastFiredRef = useRef(0);

  useEffect(() => {
    function maybeFire(): void {
      const now = Date.now();
      if (now - lastFiredRef.current < throttleMs) return;
      lastFiredRef.current = now;
      void cbRef.current();
    }

    function onVisibilityChange(): void {
      if (document.visibilityState === "visible") maybeFire();
    }

    function onFocus(): void {
      maybeFire();
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
    };
  }, [throttleMs]);
}
